import { createModel } from "@rematch/core";
import { api } from "api";
import { CreateSimpleUserCommand, UserListItemVm } from "api/generated/lumen";
import { RootModel } from "app/store";
import { mapToGetAllQuery } from "utils";
import {
  ListRequestParams,
  OrderByType,
  mergeListParams,
  mergeListParamsWithPagination,
} from "widgets/table/useTableUtils";

interface UsersListSettingsSettingsState {
  users?: UserListItemVm[];
  usersListParams: ListRequestParams;
  editingUserId?: string;
}

const initialState: UsersListSettingsSettingsState = {
  users: [],
  usersListParams: {
    pageSize: 10,
    order: [
      {
        orderBy: "username",
        orderByType: OrderByType.Ascending,
      },
    ],
  },
  editingUserId: undefined,
};

export const usersListSettings = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setUsers(
      state,
      {
        data,
        listParams,
      }: { data: UserListItemVm[]; listParams: ListRequestParams }
    ) {
      state.users = data;
      state.usersListParams = listParams;
    },
    resetUsersListParams(state) {
      state.usersListParams = {
        ...initialState.usersListParams,
        order: initialState.usersListParams.order,
      };
    },
    setEditingUserId(state, data?: string) {
      state.editingUserId = data;
    },
    resetEditingUserData(state) {
      state.editingUserId = undefined;
    },

    reset: () => initialState,
  },
  effects: (dispatch) => {
    const { usersListSettings } = dispatch;

    return {
      async fetchUsers(params: ListRequestParams = {}, state): Promise<void> {
        const mergedParams = mergeListParams(
          state.usersListSettings.usersListParams,
          params
        );

        const data = await api.lumen.users.apiUserGetAllPost({
          getAllUsersQuery: mapToGetAllQuery(mergedParams),
        });

        const { items, ...pagination } = data;

        dispatch(
          usersListSettings.setUsers({
            data: items || [],
            listParams: mergeListParamsWithPagination(mergedParams, pagination),
          })
        );
      },

      async createUser(values: CreateSimpleUserCommand): Promise<void> {
        await api.lumen.users.apiUserPost({ createSimpleUserCommand: values });
      },

      async editUser({ id, ...rest }): Promise<void> {
        await api.lumen.users.apiUserIdPut({
          id: id,
          updateUserDto: rest,
        });
      },

      async deleteUser(id: string): Promise<void> {
        await api.lumen.users.apiUserIdDelete({ id });
      },

      async inviteUser(id: string): Promise<void> {
        await api.lumen.users.apiUserInviteUserPost({ id });
      },
    };
  },
});
